// src/CountersignPage.js
// Jeff's counter-signature page — accessed via /countersign/:jeffToken from email link.

import { useState, useEffect, useRef, useCallback } from "react";

function btnStyle(bg, border, color, extra = {}) {
  return { padding: "8px 20px", background: bg, border: `1.5px solid ${border}`, borderRadius: 7, color, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif", ...extra };
}

// ─── Signature Canvas ─────────────────────────────────────────────────────────

function SignatureCanvas({ canvasRef, width = 520, height = 160 }) {
  const drawing = useRef(false);
  const lastPos = useRef(null);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) return {
      x: (e.touches[0].clientX - rect.left) * scaleX,
      y: (e.touches[0].clientY - rect.top)  * scaleY,
    };
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top)  * scaleY,
    };
  };

  const startDraw = (e) => { e.preventDefault(); drawing.current = true; lastPos.current = getPos(e); };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    const pos    = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = "round";
    ctx.lineJoin    = "round";
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDraw = () => { drawing.current = false; };

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ display: "block", width: "100%", cursor: "crosshair", touchAction: "none" }}
      onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
      onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
    />
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CountersignPage({ jeffToken }) {
  const [state,         setState]         = useState("loading"); // loading | ready | done | already_signed | error
  const [draftInfo,     setDraftInfo]     = useState(null);
  const [errorMsg,      setErrorMsg]      = useState("");
  const [saveDefault,   setSaveDefault]   = useState(false);
  const [submitting,    setSubmitting]    = useState(false);
  const [hasSaved,      setHasSaved]      = useState(false); // canvas has content
  const [usingSaved,    setUsingSaved]    = useState(false); // pre-filled from saved sig
  const canvasRef = useRef(null);

  // Load page data on mount
  useEffect(() => {
    fetch(`/api/countersign/${jeffToken}`)
      .then(r => r.json())
      .then(data => {
        if (data.error)         { setErrorMsg(data.error); setState("error"); return; }
        if (data.alreadySigned) { setDraftInfo(data); setState("already_signed"); return; }
        setDraftInfo(data);
        if (data.savedSignature) {
          // Pre-fill canvas with saved signature after render
          setTimeout(() => loadImageOntoCanvas(data.savedSignature), 50);
        }
        setState("ready");
      })
      .catch(() => { setErrorMsg("Could not load agreement. Please check your link."); setState("error"); });
  }, [jeffToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadImageOntoCanvas = useCallback((dataUrl) => {
    const canvas = canvasRef.current;
    if (!canvas || !dataUrl) return;
    const img = new window.Image();
    img.onload = () => {
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      setHasSaved(true);
      setUsingSaved(true);
    };
    img.src = dataUrl;
  }, []);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasSaved(false);
    setUsingSaved(false);
  };

  const restoreSaved = () => {
    clearCanvas();
    if (draftInfo?.savedSignature) {
      setTimeout(() => loadImageOntoCanvas(draftInfo.savedSignature), 10);
    }
  };

  const getSignatureDataUrl = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const pixels = canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height).data;
    const hasContent = Array.from(pixels).some((p, i) => i % 4 === 3 && p > 0);
    if (!hasContent) return null;
    // Flatten to white before JPEG (no alpha channel)
    const flat = document.createElement("canvas");
    flat.width = canvas.width; flat.height = canvas.height;
    const fctx = flat.getContext("2d");
    fctx.fillStyle = "#ffffff";
    fctx.fillRect(0, 0, flat.width, flat.height);
    fctx.drawImage(canvas, 0, 0);
    return flat.toDataURL("image/jpeg", 0.7);
  };

  const handleSubmit = async () => {
    const jeffSignature = getSignatureDataUrl();
    if (!jeffSignature) { alert("Please draw your signature first."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/countersign/${jeffToken}`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ jeffSignature, jeffName: "Jeffery P. Robbins", saveSignature: saveDefault }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Submission failed");
      setState("done");
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Layout helpers ────────────────────────────────────────────────────────

  const card = (children) => (
    <div style={{ minHeight: "100vh", background: "#f4f5f7", display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 40, paddingBottom: 60, fontFamily: "Arial, Helvetica, sans-serif" }}>
      {/* Header */}
      <div style={{ width: "100%", maxWidth: 600, background: "linear-gradient(160deg,#12285a 0%,#1a3a6e 100%)", borderRadius: "10px 10px 0 0", padding: "18px 28px", display: "flex", alignItems: "center", gap: 14 }}>
        <img src="/logo.png" alt="DHIT" style={{ height: 36, objectFit: "contain" }} onError={e => { e.target.style.display = "none"; }} />
        <div>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, letterSpacing: "-0.2px" }}>Agreement Counter-Signature</div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, marginTop: 2 }}>Dynamic Health IT, Inc.</div>
        </div>
      </div>
      {/* Body */}
      <div style={{ width: "100%", maxWidth: 600, background: "#fff", border: "1px solid #e8e8e8", borderTop: "none", borderRadius: "0 0 10px 10px", padding: "32px 32px 28px", boxSizing: "border-box" }}>
        {children}
      </div>
    </div>
  );

  // ── States ────────────────────────────────────────────────────────────────

  if (state === "loading") return card(
    <div style={{ textAlign: "center", color: "#888", fontSize: 14 }}>Loading…</div>
  );

  if (state === "error") return card(
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: "#1a3a6e", marginBottom: 8 }}>Unable to Load Agreement</div>
      <p style={{ color: "#666", fontSize: 14, lineHeight: 1.6 }}>{errorMsg}</p>
    </div>
  );

  if (state === "already_signed") return card(
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: "#1D9E75", marginBottom: 8 }}>Already Countersigned</div>
      <p style={{ color: "#666", fontSize: 14, lineHeight: 1.6 }}>
        The agreement for <strong>{draftInfo?.customerName}</strong> has already been countersigned and fully executed.
      </p>
    </div>
  );

  if (state === "done") return card(
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: "#1D9E75", marginBottom: 8 }}>Agreement Countersigned</div>
      <p style={{ color: "#666", fontSize: 14, lineHeight: 1.7 }}>
        The agreement for <strong>{draftInfo?.customerName}</strong> is now fully executed.<br/>
        The client and your team have been emailed the final signed copy.
      </p>
    </div>
  );

  // ── Ready state — the actual signing form ─────────────────────────────────

  const signedDate = draftInfo?.clientSignedAt
    ? new Date(draftInfo.clientSignedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "—";

  return card(<>
    <div style={{ fontSize: 17, fontWeight: 700, color: "#1a3a6e", marginBottom: 4 }}>
      Your countersignature is needed
    </div>
    <p style={{ margin: "0 0 20px", fontSize: 14, color: "#555", lineHeight: 1.6 }}>
      The client has signed. Please review the agreement and add your signature below to fully execute it.
    </p>

    {/* Agreement summary */}
    <div style={{ background: "#f8f9fc", border: "1px solid #e3e8f0", borderRadius: 8, padding: "14px 18px", marginBottom: 20, fontSize: 13 }}>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", color: "#444" }}>
        <span style={{ color: "#888", fontWeight: 500 }}>Client</span>
        <span style={{ fontWeight: 700, color: "#1a3a6e" }}>{draftInfo?.customerName}</span>
        {draftInfo?.products?.length > 0 && <>
          <span style={{ color: "#888", fontWeight: 500 }}>Products</span>
          <span>{draftInfo.products.join(", ")}</span>
        </>}
        <span style={{ color: "#888", fontWeight: 500 }}>Signed by client</span>
        <span>{draftInfo?.signerName || "—"} on {signedDate}</span>
      </div>
    </div>

    {/* View PDF link */}
    <div style={{ marginBottom: 24 }}>
      <a
        href={`/api/countersign/${jeffToken}/pdf`}
        target="_blank"
        rel="noreferrer"
        style={{ color: "#1a3a6e", fontSize: 13, fontWeight: 600, textDecoration: "underline" }}
      >
        View client-signed agreement (PDF) →
      </a>
    </div>

    {/* Signature canvas */}
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#333", marginBottom: 6 }}>
        Your Signature <span style={{ fontWeight: 400, color: "#888" }}>(Jeffery P. Robbins)</span>
      </div>
      <div style={{ border: "1.5px solid #ddd", borderRadius: 8, background: "#fafafa", overflow: "hidden", position: "relative" }}>
        <SignatureCanvas canvasRef={canvasRef} />
        {!hasSaved && (
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", color: "#ccc", fontSize: 13, pointerEvents: "none", userSelect: "none" }}>
            Draw your signature here
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        <button onClick={clearCanvas} style={btnStyle("#fff", "#ddd", "#555")}>Clear</button>
        {draftInfo?.savedSignature && !usingSaved && (
          <button onClick={restoreSaved} style={btnStyle("#fff", "#1a3a6e", "#1a3a6e")}>Use Saved Signature</button>
        )}
      </div>
    </div>

    {/* Save as default checkbox */}
    <label style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", fontSize: 13, color: "#444", marginBottom: 28 }}>
      <input
        type="checkbox"
        checked={saveDefault}
        onChange={e => setSaveDefault(e.target.checked)}
        style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#1a3a6e" }}
      />
      Save this as my default signature for future agreements
    </label>

    {/* Submit */}
    <button
      onClick={handleSubmit}
      disabled={submitting}
      style={btnStyle("#1a3a6e", "#1a3a6e", "#fff", { width: "100%", padding: "12px 0", fontSize: 15, opacity: submitting ? 0.6 : 1 })}
    >
      {submitting ? "Signing…" : "Sign & Finalize Agreement →"}
    </button>
  </>);
}
